import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { authedFileUrl } from "@/components/propertyDetail/helpers";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { toast } from "sonner";
import { X, Check, Sparkle, ArrowsClockwise, Spinner, Warning, Sun, Shield, Broom } from "@phosphor-icons/react";

/**
 * PhotoReviewModal — iter v28 r306
 *
 * Mostra le foto migliorate del kit post-sopralluogo PRIMA che Pronto
 * generi i contenuti social. L'agente:
 *  - vede prima/dopo (slider)
 *  - può rigenerare singola foto con 3 preset (Luminosa / Fedele / Riordina)
 *  - approva tutte → il kit prosegue automaticamente
 *
 * Props:
 *  - propertyId: string
 *  - open: boolean
 *  - onClose: () => void
 *  - onApproved: () => void (callback dopo approvazione, per refresh tab)
 */
export default function PhotoReviewModal({ propertyId, open, onClose, onApproved }) {
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [regenerating, setRegenerating] = useState({}); // {photoId: true/preset}
  const [approving, setApproving] = useState(false);

  const fetchReview = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      // iter v28 r393 — Fix doppio `/api/api/`: `api` ha già baseURL=${API}.
      const r = await api.get(`/properties/${propertyId}/photo-review`);
      setReviewData(r.data);
    } catch (e) {
      // iter v28 r375/r380 — User M1285/M1290: l'errore "Not Found" appariva
      // anche quando l'immobile non aveva foto (caso legittimo, es. annuncio
      // creato da sopralluogo vocale senza scatti). Silenziamo il 404 e
      // lasciamo lo stato "0 foto" che mostra empty-state pulito.
      const status = e?.response?.status;
      if (status !== 404) {
        toast.error("Non sono riuscito a caricare la revisione foto", { description: apiError(e) });
      } else {
        setReviewData({ photos: [], presets: [], enhanced_count: 0, drift_count: 0, status: "none" });
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (open) fetchReview();
  }, [open, fetchReview]);

  const handleRegenerate = async (photoId, preset, presetLabel) => {
    setRegenerating((prev) => ({ ...prev, [photoId]: preset }));
    try {
      await api.post(`/photos/${photoId}/regenerate`, { preset });
      toast.success(`Foto rigenerata · ${presetLabel}`);
      await fetchReview();
    } catch (e) {
      toast.error("Non sono riuscito a rigenerare la foto", { description: apiError(e) });
    } finally {
      setRegenerating((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const r = await api.post(`/properties/${propertyId}/photo-review/approve`, {});
      const resumed = r.data?.resumed_jobs || 0;
      if (resumed > 0) {
        toast.success("Foto approvate", {
          description: `Pronto sta preparando ${resumed} ${resumed === 1 ? "contenuto" : "contenuti"} per i social. Riceverai una notifica quando è pronto.`,
        });
      } else {
        toast.success("Foto approvate");
      }
      if (onApproved) onApproved();
      onClose();
    } catch (e) {
      toast.error("Non sono riuscito ad approvare", { description: apiError(e) });
    } finally {
      setApproving(false);
    }
  };

  if (!open) return null;

  const photos = reviewData?.photos || [];
  const presets = reviewData?.presets || [];
  const enhancedCount = reviewData?.enhanced_count || 0;
  const driftCount = reviewData?.drift_count || 0;
  const status = reviewData?.status || "none";
  const alreadyApproved = status === "approved";

  // Icona per preset
  const presetIcon = (id) => {
    if (id === "luminosa") return <Sun size={14} weight="bold" />;
    if (id === "fedele") return <Shield size={14} weight="bold" />;
    if (id === "riordina") return <Broom size={14} weight="bold" />;
    return <ArrowsClockwise size={14} weight="bold" />;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch sm:items-center sm:justify-center sm:p-4"
      data-testid="photo-review-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-review-title"
    >
      <div className="bg-[#FAF8F3] w-full sm:max-w-5xl sm:max-h-[90vh] sm:rounded-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#1C1C1A]/10 bg-white flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#6B6A65] font-medium">
              <Sparkle size={12} weight="fill" className="text-[#1C1C1A]" />
              <span>Revisione foto del kit</span>
            </div>
            <h2
              id="photo-review-title"
              className="text-xl sm:text-2xl font-semibold text-[#1C1C1A] mt-1 leading-tight"
              data-testid="photo-review-title"
            >
              {alreadyApproved
                ? "Hai già confermato queste foto"
                : "Controlla le foto prima dei contenuti social"}
            </h2>
            <p className="text-sm text-[#6B6A65] mt-1">
              {alreadyApproved
                ? "Le foto sono state usate per generare i contenuti."
                : `Pronto ha migliorato ${enhancedCount} ${enhancedCount === 1 ? "foto" : "foto"}. Approvale o rigenera quelle che non ti convincono.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full hover:bg-[#1C1C1A]/5 flex items-center justify-center text-[#1C1C1A]"
            aria-label="Chiudi"
            data-testid="photo-review-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Alert drift */}
        {driftCount > 0 && (
          <div className="mx-5 sm:mx-6 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex items-start gap-2"
               data-testid="photo-review-drift-banner">
            <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <strong className="font-semibold">Pronto ha notato qualcosa</strong>: {driftCount}{" "}
              {driftCount === 1 ? "foto potrebbe avere" : "foto potrebbero avere"} una piccola
              incongruenza rispetto all'originale. Ti consiglio di rigenerarla con
              "Più fedele all'originale".
            </div>
          </div>
        )}

        {/* Body — lista foto */}
        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size={28} className="animate-spin text-[#6B6A65]" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 text-[#6B6A65] text-sm">
              Non ci sono foto da rivedere per questo immobile.
            </div>
          ) : (
            photos.map((p) => (
              <PhotoReviewCard
                key={p.photo_id}
                photo={p}
                presets={presets}
                regenerating={regenerating[p.photo_id]}
                onRegenerate={handleRegenerate}
                presetIcon={presetIcon}
                disabled={alreadyApproved}
              />
            ))
          )}
        </div>

        {/* Footer — approva */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-[#1C1C1A]/10 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-[#6B6A65]">
            {alreadyApproved
              ? "Hai già confermato queste foto."
              : "Quando confermi, Pronto inizierà a preparare i contenuti social usando queste foto."}
          </div>
          {!alreadyApproved && (
            <button
              type="button"
              onClick={photos.length === 0 ? () => { if (onApproved) onApproved(); onClose(); } : handleApprove}
              disabled={approving}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1C1A] text-white text-sm font-medium hover:bg-[#1C1C1A]/90 disabled:opacity-50 transition-colors"
              data-testid="photo-review-approve-all"
            >
              {approving ? (
                <Spinner size={16} className="animate-spin" />
              ) : (
                <Check size={16} weight="bold" />
              )}
              {approving ? "Confermo..." : (photos.length === 0 ? "Vai ai contenuti social" : "Va bene, procedi coi contenuti")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Card singola foto ─────────────────────────────────────────── */

function PhotoReviewCard({ photo, presets, regenerating, onRegenerate, presetIcon, disabled }) {
  const beforeUrl = photo.original_path ? authedFileUrl(photo.original_path) : null;
  const afterUrl = photo.enhanced_path ? authedFileUrl(photo.enhanced_path) : null;

  return (
    <div
      className="bg-white rounded-2xl border border-[#1C1C1A]/8 overflow-hidden"
      data-testid={`photo-review-card-${photo.photo_id}`}
    >
      <div className="aspect-[4/3] bg-[#1C1C1A]/5 relative">
        {beforeUrl && afterUrl ? (
          <BeforeAfterSlider beforeUrl={beforeUrl} afterUrl={afterUrl} />
        ) : afterUrl ? (
          <img src={afterUrl} alt="foto migliorata" className="w-full h-full object-cover" />
        ) : beforeUrl ? (
          <img src={beforeUrl} alt="originale" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm text-[#6B6A65]">
            Foto non disponibile
          </div>
        )}
        {photo.drift && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
               data-testid={`photo-review-drift-badge-${photo.photo_id}`}>
            <Warning size={11} weight="fill" />
            Da rivedere
          </div>
        )}
      </div>
      <div className="px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-xs uppercase tracking-wider text-[#6B6A65] font-medium">
            {photo.room_type && photo.room_type !== "altro"
              ? photo.room_type
              : (photo.label || "Foto")}
            {photo.regenerate_count > 0 && (
              <span className="ml-2 normal-case tracking-normal text-[#1C1C1A]/40">
                · rigenerata {photo.regenerate_count}×
              </span>
            )}
          </div>
        </div>
        {photo.drift_warnings && photo.drift_warnings.length > 0 && (
          <ul className="mb-3 text-[12px] text-amber-800 list-disc list-inside">
            {photo.drift_warnings.slice(0, 2).map((w, idx) => (
              <li key={idx}>{w}</li>
            ))}
          </ul>
        )}
        {!disabled && (
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => {
              const busy = regenerating === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => onRegenerate(photo.photo_id, preset.id, preset.label)}
                  disabled={!!regenerating}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1C1C1A]/5 hover:bg-[#1C1C1A]/10 text-[#1C1C1A] text-[12px] font-medium transition-colors disabled:opacity-50"
                  data-testid={`photo-review-regenerate-${preset.id}-${photo.photo_id}`}
                >
                  {busy ? (
                    <Spinner size={12} className="animate-spin" />
                  ) : (
                    presetIcon(preset.id)
                  )}
                  {busy ? "Rigenero..." : preset.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
